/**
 * Declarative field definitions consumed by the generic <SearchBar /> component
 * (app/src/components/ui/SearchBar). Each entry describes a filter control and,
 * optionally, the query-param it maps to (`paramKey`).
 *
 * Field config schema:
 *   field:       string  (required) — control key / default query-param name
 *   label:       string
 *   type:        'text' | 'number' | 'date' | 'dateRange' | 'select' | 'autocomplete'
 *   placeholder: string
 *   paramKey:    string  — API param name if different from `field`
 *   width:       number
 *   options:     [{label, value}] (select) | [{id, label}] (autocomplete)
 *
 * Autocomplete fields that need runtime data (loadOptions) are built inside the
 * component that owns the API request, not here.
 */

export const clientSearchFields = [
  {field: "name", label: "Nombre", type: "text", placeholder: "Buscar por nombre...", width: 200}
];

export const userSearchFields = [
  {field: "name", label: "Nombre", type: "text", width: 130},
  {field: "surname", label: "Apellidos", type: "text", width: 130},
  {field: "email", label: "Email", type: "text", width: 140},
  {field: "ts_email", label: "TS Email", type: "text", width: 130},
  {
    field: "status",
    label: "Estado",
    type: "select",
    width: 120,
    options: [
      {label: "Activos", value: "active"},
      {label: "Desactivados", value: "inactive"}
    ]
  },
  {
    field: "tsIdLinked",
    label: "Vinculado a TS ID",
    type: "select",
    width: 180,
    options: [
      {label: "Sí", value: "true"},
      {label: "No", value: "false"}
    ]
  },
  {field: "created_at", label: "Creado", type: "dateRange", width: 120}
];

export const PRODUCT_SYNC_TYPE_OPTIONS = [
  {label: "Manual", value: "manual"},
  {label: "Sync", value: "sync"},
  {label: "Auto", value: "auto"}
];

export const productSearchFields = [
  {field: "code", label: "Código", type: "text", width: 160},
  {field: "name", label: "Nombre", type: "text", width: 160}
];

export const tenantSearchFields = [
  {field: "name", label: "Nombre", type: "text", width: 160},
  {field: "nif", label: "NIF", type: "text", width: 130},
  {field: "internal_code", label: "Código interno", type: "text", width: 160},
  {
    field: "linked_users",
    label: "Estado de usuarios",
    type: "select",
    width: 220,
    options: [
      {label: "Con usuarios", value: "true"},
      {label: "Sin usuarios", value: "false"}
    ]
  },
  {
    // Opciones de roles: se cargan del backend en Tenants.jsx (loadOptions). El valor enviado es el nombre del rol.
    field: "only_linked_role",
    label: "Rol vinculado (exclusivo)",
    type: "autocomplete",
    filterMode: "server",
    freeSolo: true,
    width: 240,
    tooltip:
      "Filtra los tenants en los que TODOS los usuarios vinculados tienen este rol (no basta con que alguno lo tenga)",
    mapValue: (option) => option.label
  },
  {field: "created_at", label: "Creado", type: "dateRange", width: 120}
];

// userId y clientId los exige el backend como UUID (no texto libre), por lo que se
// resuelven como autocomplete server-side (email -> id) directamente en Tokens.jsx.
// clientId se deja pendiente hasta que el endpoint de tokens acepte client_name.
export const tokenSearchFields = [
  {
    field: "type",
    label: "Tipo",
    type: "select",
    width: 120,
    options: [
      {label: "Refresh", value: "refresh"},
      {label: "Reset", value: "reset"},
      {label: "Register", value: "register"}
    ]
  },
  {field: "expires_at", label: "Expira", type: "dateRange", width: 120}
];

export const m2mTokenSearchFields = [{field: "domain", label: "Domain", type: "text", width: 160}];

export const ticketServerSearchFields = [
  {
    field: "ticket_type",
    label: "Tipo",
    type: "select",
    width: 150,
    options: [
      {label: "Registro", value: "registration"},
      {label: "Invitación", value: "invitation"}
    ]
  },
  {field: "email", label: "Email", type: "text", width: 180},
  {field: "tenant_nif", label: "NIF Empresa", type: "text", width: 140}
];

export const TICKET_SERVER_FILTER_KEYS = ticketServerSearchFields.map((field) => field.paramKey ?? field.field);

/** Fixed option sets for scope filters (client scopes). */
export const SCOPE_ACTION_OPTIONS = [
  {label: "read", value: "read"},
  {label: "write", value: "write"},
  {label: "delete", value: "delete"},
  {label: "read-roles", value: "read-roles"},
  {label: "write-roles", value: "write-roles"},
  {label: "delete-roles", value: "delete-roles"}
];

export const SCOPE_OWNERSHIP_OPTIONS = [
  {label: "global", value: "global"},
  {label: "tenant", value: "tenant"},
  {label: "self", value: "self"}
];
